/**
 * Recovery for lazy chunks that went missing because a new build was deployed.
 *
 * Every build content-hashes its chunk filenames, so a tab opened before a deploy
 * asks for files the server no longer has. Retrying the same URL can never succeed
 * — only a fresh `index.html` carries the new names — so the single useful action
 * is one reload.
 */
import { isChunkLoadError } from './isChunkLoadError';
import { reloadPage } from './reloadPage';

/** Marks that a reload has already been spent recovering a stale chunk in this tab. */
export const CHUNK_RELOAD_KEY = 'docufy:chunk-reloaded';

function hasSpentChunkReload(): boolean {
  try {
    // Truthiness rather than `!== null`: storage shims hand back `undefined` for a
    // missing key, which would otherwise read as "already reloaded".
    return Boolean(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
  } catch {
    // Safari private mode throws on sessionStorage access; treating that as
    // "already spent" means a failed read can never become a reload loop.
    return true;
  }
}

/**
 * Reload once when `error` is a missing chunk, otherwise do nothing.
 *
 * The session flag caps this at one reload per tab, so a chunk that fails for a
 * reason a reload cannot fix (offline, a persistent 500) surfaces to the error
 * boundary instead of reloading forever.
 */
export function recoverFromStaleChunk(error: unknown): void {
  if (!isChunkLoadError(error) || hasSpentChunkReload()) {
    return;
  }
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Without a durable marker a reload could repeat indefinitely, so skip it.
    return;
  }
  reloadPage();
}

/**
 * Clears the one-reload marker for this tab.
 *
 * Leave the marker set for the tab lifetime after a recovery reload so a still-
 * missing chunk cannot loop. Call this only when a caller has confirmed a fresh
 * build actually loaded (not merely that the shell mounted).
 */
export function clearChunkReloadMarker(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
